import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Trash2, XCircle, AlertCircle, CheckCircle2, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { api, ApiError, type ScheduledPost } from '@/lib/api'
import { formatAbsolute, formatRelative } from '@/lib/utils'

export default function Schedule() {
  const [posts, setPosts] = useState<ScheduledPost[]>([])
  const [loading, setLoading] = useState(true)

  async function load() {
    setLoading(true)
    try {
      setPosts(await api.listScheduled())
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  async function cancel(id: string) {
    try {
      await api.updateScheduled(id, { status: 'canceled' })
      toast.success('Canceled')
      load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not cancel')
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this scheduled post?')) return
    try {
      await api.deleteScheduled(id)
      toast.success('Deleted')
      load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete')
    }
  }

  const upcoming = posts.filter((p) => p.status === 'scheduled' || p.status === 'sending')
  const done = posts.filter((p) => p.status !== 'scheduled' && p.status !== 'sending')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Schedule</h1>
        <p className="text-muted-foreground">Posts fire via cron, rounded to the nearest 5 minutes.</p>
      </div>

      <Section title="Upcoming" desc={`${upcoming.length} waiting to fire`}>
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && upcoming.length === 0 && <Empty text="Nothing upcoming. Go draft a thread." />}
        {upcoming.map((p) => (
          <PostCard key={p.id} post={p} onCancel={() => cancel(p.id)} onDelete={() => remove(p.id)} />
        ))}
      </Section>

      <Section title="History" desc="Sent, failed, and canceled">
        {!loading && done.length === 0 && <Empty text="No history yet." />}
        {done.map((p) => (
          <PostCard key={p.id} post={p} onDelete={() => remove(p.id)} />
        ))}
      </Section>
    </div>
  )
}

function Section({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{desc}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2.5">{children}</CardContent>
    </Card>
  )
}

function Empty({ text }: { text: string }) {
  return <p className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">{text}</p>
}

function PostCard({
  post,
  onCancel,
  onDelete,
}: {
  post: ScheduledPost
  onCancel?: () => void
  onDelete: () => void
}) {
  return (
    <div className="rounded-md border bg-background p-4">
      <div className="mb-2 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 uppercase tracking-wide text-muted-foreground">
          <StatusPill status={post.status} />
          <span>{post.kind}</span>
          {post.replyToTweetId && <span>reply</span>}
        </div>
        <div className="flex items-center gap-3 text-muted-foreground">
          <span title={formatAbsolute(post.fireAt)}>{formatRelative(post.fireAt)}</span>
          {onCancel && post.status === 'scheduled' && (
            <button onClick={onCancel} className="hover:text-destructive" title="Cancel">
              <XCircle className="h-4 w-4" />
            </button>
          )}
          {post.status !== 'sent' && (
            <button onClick={onDelete} className="hover:text-destructive" title="Delete">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {post.tweets.map((t, i) => (
          <div key={i} className="whitespace-pre-wrap rounded bg-muted/50 px-3 py-2 text-sm">
            {t}
          </div>
        ))}
      </div>
      {post.lastError && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-destructive">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-none" />
          <span>{post.lastError}</span>
        </div>
      )}
    </div>
  )
}

function StatusPill({ status }: { status: ScheduledPost['status'] }) {
  const map = {
    scheduled: { icon: Clock, cls: 'text-sky-700 dark:text-sky-300' },
    sending: { icon: Clock, cls: 'text-amber-700 dark:text-amber-300' },
    sent: { icon: CheckCircle2, cls: 'text-emerald-700 dark:text-emerald-300' },
    failed: { icon: AlertCircle, cls: 'text-destructive' },
    canceled: { icon: XCircle, cls: 'text-muted-foreground' },
  } as const
  const v = map[status]
  const Icon = v.icon
  return (
    <span className={`inline-flex items-center gap-1 ${v.cls}`}>
      <Icon className="h-3 w-3" />
      {status}
    </span>
  )
}
